import { inject, Injectable } from '@angular/core';
import { Store, Actions, ofActionDispatched, ofActionSuccessful } from '@ngxs/store';
import { LoginState } from '@pages/login/store/state/login.state';
import { LoginStep } from '@pages/login/models/login-step.enum';
import { CheckMfa, LoadLoginConfiguration, LogIn, MfaTokenExpiredError, UpdateStep } from '@pages/login/store/actions/login.actions';
import { Credentials } from '@pages/login/models/login-flow/credentials.interface';
import { LoginConfiguration } from '@pages/login/models/login-configuration/login-configuration.interface';
import { ResetStateFacade } from '@core/store/facades/reset-state.facade';

@Injectable({
  providedIn: 'root',
})
export class LoginFacade {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly resetStateFacade = inject(ResetStateFacade);

  readonly currentStep = this.store.selectSignal(LoginState.currentStep);
  readonly errorMessageTranslationKey = this.store.selectSignal(LoginState.errorMessageTranslationKey);
  readonly loginConfiguration = this.store.selectSignal(LoginState.loginConfiguration);
  readonly loginConfigurationLoading = this.store.selectSignal(LoginState.loginConfigurationLoading);
  readonly logInLoading = this.store.selectSignal(LoginState.logInLoading);
  readonly checkMfaLoading = this.store.selectSignal(LoginState.checkMfaLoading);
  readonly samlLoginUrl = this.store.selectSignal(LoginState.samlLoginUrl);
  readonly loginAttempt = this.store.selectSignal(LoginState.loginAttempt);
  readonly authorizationData = this.store.selectSignal(LoginState.authorizationData);

  readonly loginConfigurationLoaded$ = this.actions$.pipe(ofActionSuccessful(LoadLoginConfiguration));
  readonly loginCompleted$ = this.actions$.pipe(ofActionSuccessful(LogIn));
  readonly checkMfaCompleted$ = this.actions$.pipe(ofActionSuccessful(CheckMfa));
  readonly mfaTokenExpiredError$ = this.actions$.pipe(ofActionDispatched(MfaTokenExpiredError));

  loadLoginConfiguration(loginConfiguration: LoginConfiguration): void {
    this.store.dispatch(new LoadLoginConfiguration(loginConfiguration));
  }

  goToLoginConfigurationStep(): void {
    this.store.dispatch(new UpdateStep(LoginStep.Configuration));
  }

  goToLoginFlowStep(): void {
    this.store.dispatch(new UpdateStep(LoginStep.Flow));
  }

  checkMfa(mfaToken: string): void {
    this.store.dispatch(new CheckMfa(mfaToken));
  }

  logIn(credentials: Credentials): void {
    this.store.dispatch(new LogIn(credentials));
  }

  clearState(): void {
    this.resetStateFacade.clearState(LoginState);
  }
}
